import * as React from 'react'

import { TextFieldProps } from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'

import { NoConditionsCard } from './NoConditionsCard'
import { ConditionsList } from './ConditionsList'
import { FormSection } from '@sales-center/components'
import { ConditionFormPopup } from './ConditionsForm'
import { EditCoverageBlockDialog } from '../../EditCoverageBlockDialog'
import { ScrollToErrorMarker } from '@user/components/Formik/ScrollToErrorMarker'

import { useData, useHandlers } from '../hooks'
import { useTheme } from '@mui/material/styles'
import { useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback } from 'react'
import { setMapViewDialogOpen } from '@user/store/map/slice'
import {
  startTerritoryDrawForRule,
  startTerritoryRedrawForRule,
} from '@user/store/territoryDraw'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'
import { selectCurrentUser } from '@user/store/authorization'
import type {
  ConditionModel,
  LeadAssignmentRuleFormModel,
} from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'

interface Props {
  textFieldProps: TextFieldProps
  name: string
  spacing?: number
  padding?: number
}

export function Conditions(props: Props) {
  const { spacing = 2, textFieldProps, name } = props
  const {
    errors,
    touched,
    assignmentRuleConditions,
    assignmentRuleConditionsSelectedAttributes,
  } = useData()
  const {
    saveCondition,
    editValues,
    setOpen,
    removeCondition,
    editCondition,
    open,
    cancelCondition,
    addCondition,
    coverageBlockLabels,
    closeCoverageBlock,
  } = useHandlers()
  const showError = Boolean(
    errors?.assignmentRuleConditions && touched?.assignmentRuleConditions,
  )
  const theme = useTheme()

  // Drawing a new territory for this rule (WWM-17508). The map is where a territory gets drawn
  // -- there is no draw button on it any more -- so the rule parks itself, sends the admin
  // there, and picks up where it left off when they come back.
  const dispatch = useDispatch()
  const { values } = useFormikContext<LeadAssignmentRuleFormModel>()
  const territoriesEnabled = useD2DTerritoriesEnabled()
  const { isAdmin } = useSelector(selectCurrentUser)

  const drawNewTerritory = useCallback(
    (condition: ConditionModel) => {
      // The whole form, not just the condition: they may have typed a name and description and
      // chosen a team already, and losing that to draw a shape would be its own bug.
      // ruleId is null while creating; what matters is that the parked form can be matched back
      // to the rule it came from, so it is never applied to a different one.
      dispatch(
        startTerritoryDrawForRule({
          rule: values as unknown as Record<string, unknown>,
          condition: condition as unknown as Record<string, unknown>,
          ruleId: values.id ?? null,
        }),
      )
      // 'sidenav' rather than a new source: it is the "opened from navigation" path, which
      // frames the map on wherever the admin last left it.
      dispatch(setMapViewDialogOpen({ open: true, source: 'sidenav' }))
    },
    [dispatch, values],
  )

  const redrawTerritory = useCallback(
    (condition: ConditionModel, territoryId: number) => {
      dispatch(
        startTerritoryRedrawForRule({
          rule: values as unknown as Record<string, unknown>,
          condition: condition as unknown as Record<string, unknown>,
          territoryId,
          ruleId: values.id ?? null,
        }),
      )
      dispatch(setMapViewDialogOpen({ open: true, source: 'sidenav' }))
    },
    [dispatch, values],
  )

  // Admin-only, matching the territory write endpoints. A non-admin picking from existing
  // territories is fine; drawing or reshaping one would 403 on save.
  const canDraw = territoriesEnabled && isAdmin
  return (
    <FormSection
      label={'Create Conditions'}
      spacing={spacing}
      flexDirection="column"
      maxWidth="626px"
    >
      {showError && (
        <FormHelperText sx={{ paddingLeft: theme.spacing(3.4) }} error required>
          Required
        </FormHelperText>
      )}
      <Grid
        container
        item
        direction="row"
        justifyContent="space-between"
        maxWidth="626px"
      >
        <Grid item xs={8}>
          <Box>
            <Typography
              variant="body2"
              display="inline"
              color={theme.palette.text.primary}
            >
              All conditions below will be used to automatically assign an owner
              when a new lead is created.
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={4}>
          <Box display="flex" alignItems="flex-end" justifyContent="flex-end">
            <Button
              color="primary"
              variant="outlined"
              size="large"
              data-test-id="addConditionBtn"
              onClick={addCondition}
              sx={{
                fontFamily: 'Source Sans Pro Semibold',
                border: '1px solid #005AA3',
                borderRadius: '4px',
              }}
            >
              Add Condition
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {assignmentRuleConditions.length == 0 ? (
          <NoConditionsCard />
        ) : (
          <ConditionsList
            conditions={assignmentRuleConditions}
            removeCondition={removeCondition}
            editCondition={editCondition}
          />
        )}
      </Grid>
      <ConditionFormPopup
        open={open}
        setOpen={setOpen}
        save={saveCondition}
        cancel={cancelCondition}
        textFieldProps={textFieldProps}
        editValues={editValues}
        assignmentRuleConditionsSelectedAttributes={
          assignmentRuleConditionsSelectedAttributes
        }
        onDrawNewTerritory={canDraw ? drawNewTerritory : undefined}
        onRedrawTerritory={canDraw ? redrawTerritory : undefined}
      />
      {/* Mounted only when needed: AlertDialogSlide is keepMounted, so an always-mounted one would sit under the condition modal. */}
      {coverageBlockLabels && (
        <EditCoverageBlockDialog
          labels={coverageBlockLabels}
          onClose={closeCoverageBlock}
        />
      )}
      {name && <ScrollToErrorMarker fieldName={name} />}
    </FormSection>
  )
}
